import i18n from '../../../i18n';


/**
 * "عروضي" (My Promos) — ported from .claude/promos-prototype.html, markup/
 * CSS verbatim per that file, NOT rebuilt (same discipline as
 * CafeCreate/fastcreate-prototype.html). The prototype's own copy was
 * Arabic-only, but this platform is EN/AR bilingual (see src/i18n.js,
 * shipped feature/i18n) — every prototype string that made it into this
 * page still needs a real English counterpart, not a fallback. An earlier
 * pass here treated "Arabic-only" as a deliberate precedent (it isn't —
 * that was an open, previously-flagged defect, fixed 2026-09-10: every
 * string, including the SHAPES/DAYPARTS/DAYS catalogs and the digit
 * formatter below, now resolves through t() or the language-aware ar()).
 *
 * ⚠️ THE GOVERNING PRINCIPLE, Mahmoud's own words: "النظام بيوزع وبيرتب وهو
 * ما عليه إشي، نكتفي بإخباره عشان ما يحس الموضوع معقد." — the system
 * arranges, the user is told, never asked. Every control this editor does
 * NOT have is deliberate, not an oversight: show duration / interval come
 * from smartDefaults(shape, designs) and RECOMPUTE on change; "أضبطه بنفسي"
 * reveals the full controls, "ارجع للضبط التلقائي" undoes it. The
 * suggestion IS the applied default — there is no "auto vs manual" mode to
 * pick, that would just reintroduce the control that was removed.
 *
 * ⚠️ REAL DIFFERENCE FROM THE PROTOTYPE, worth recording here because it is
 * not visible in the ported markup: the prototype simulates a live,
 * second-by-second "showing now" countdown client-side (a setInterval over
 * fake state). The REAL device runs its rotation entirely locally — see
 * VenueOverlayPromoRotationState / MediaScreen.handleVenueOverlayPromoTick
 * on the player — and reports nothing back. There is no live telemetry to
 * read here, so this page does NOT fake one. "Currently running" is the
 * real, stored `is_active` flag (is the schedule turned on), not "is an
 * appearance on screen this second" (unknowable from the dashboard). The
 * live-bar IS kept for "اعرضه الآن" specifically, because that one action's
 * duration is real and client-known at the moment it is pressed — it says
 * "sent", never "confirmed showing".
 *
 * ⚠️ manual_round_count has no "بلا حدّ" (unlimited) option here, unlike the
 * prototype's ROUNDS list (which includes 0 = unlimited). The player's
 * Utils.getNextVenueOverlayPromoEpochSeconds treats a null round cap as
 * "nothing to schedule, ever" for manual mode — an "unlimited" promo would
 * silently never fire. Dropped rather than shipping a control that lies.
 *
 * ⚠️ The background-removal toggle ("أزل الخلفية" in the prototype) is
 * rendered disabled with a reason — Gemini cutout isn't wired yet. Never a
 * live-looking control that does nothing.
 */

const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
/**
 * Arabic-Indic digits ONLY in Arabic — reads the live language off the
 * shared i18n singleton (same direct-import pattern as queries/loyalty.js
 * and queries/automation.js) so every pure helper below can call this
 * without threading a language param through each one. In English this is
 * a no-op passthrough; before this fix it converted unconditionally,
 * which is exactly why English mode showed Arabic-Indic numerals.
 */
const ar = (n) => {
  const s = String(n);
  return (i18n.resolvedLanguage || i18n.language || '').startsWith('ar')
    ? s.replace(/\d/g, (d) => AR_DIGITS[d])
    : s;
};

/** Layout/visual data only — NO text. Copy (label, size, format, tips) lives in i18n as promos.shape_<key>_*, read via shapeLabel()/shapeMeta() at render time. */
const SHAPES = {
  corner: {
    dia: <i style={{ insetInlineEnd: '6%', bottom: '9%', width: '26%', height: '40%' }} />,
    guideSafe: { insetInlineEnd: 0, bottom: 0, width: '38%', height: '56%' },
    guideDead: { insetInlineStart: 0, top: 0, width: '62%', height: '100%' },
    frame: { content: { inset: 0 }, promo: { insetInlineEnd: '5%', bottom: '8%', width: '22%', height: '34%' } },
  },
  bottom: {
    dia: (
      <>
        <b style={{ insetInlineStart: 0, top: 0, width: '100%', height: '78%' }} />
        <i style={{ insetInlineStart: 0, bottom: 0, width: '100%', height: '22%' }} />
      </>
    ),
    guideSafe: { insetInlineStart: 0, bottom: 0, width: '100%', height: '24%' },
    guideDead: { insetInlineStart: 0, top: 0, width: '100%', height: '76%' },
    frame: {
      content: { insetInlineStart: 0, top: 0, width: '100%', height: '80%' },
      promo: { insetInlineStart: 0, bottom: 0, width: '100%', height: '20%' },
    },
  },
  lshape: {
    // The L frame is 80% x 80% (1536x864 content, 384x864 right strip, 1920x216 bottom strip at 1920x1080).
    dia: (
      <>
        <b style={{ insetInlineStart: 0, top: 0, width: '80%', height: '80%' }} />
        <i style={{ insetInlineEnd: 0, top: 0, width: '20%', height: '80%' }} />
        <i style={{ insetInlineStart: 0, bottom: 0, width: '100%', height: '20%' }} />
      </>
    ),
    guideSafe: { insetInlineEnd: 0, top: 0, width: '20%', height: '100%' },
    guideDead: { insetInlineStart: 0, top: 0, width: '80%', height: '80%' },
    frame: {
      content: { insetInlineStart: 0, top: 0, width: '80%', height: '80%' },
      promo: { insetInlineEnd: 0, top: 0, width: '20%', height: '100%' },
      promo2: { insetInlineStart: 0, bottom: 0, width: '80%', height: '20%' },
    },
  },
};

const shapeLabel = (t, key) => t(`promos.shape_${key}_label`);
const shapeMeta = (t, key) => ({
  size: t(`promos.shape_${key}_size`),
  fmt: t(`promos.shape_${key}_fmt`),
  ratio: t(`promos.shape_${key}_ratio`),
  weight: t(`promos.shape_${key}_weight`),
  tip: t(`promos.shape_${key}_tip`),
  tips: [t(`promos.shape_${key}_tip_1`), t(`promos.shape_${key}_tip_2`), t(`promos.shape_${key}_tip_3`)],
});

/**
 * k/from/to only — label and subtext are translated at render time via
 * daypartLabel()/daypartSub().
 *
 * ⚠️ morn/noon/eve/all ARE SHORTCUTS, NOT THE ONLY OPTIONS (2026-09-10 —
 * found on real hardware: a café open past 23:00, or 24h, had NO usable
 * automatic window at all — every preset assumed a venue that closes at
 * night). 'all' used to be mislabeled: "طوال اليوم" (all day) but only ran
 * 08:00–23:00, not actually 24 hours — fixed to genuinely be the full day.
 * 'custom' is the real fix: free-form from/to, so a late-night or 24h venue
 * is never assumption-blocked by a fixed catalog again. Same storage shape
 * as every other preset — see toPayload, unchanged: whatever `from`/`to`
 * end up as, automatic or custom, write to the SAME start_time/end_time
 * columns. 'man' is not a daypart at all — it's the switch to manual mode,
 * kept last since it's a different axis (mode, not schedule).
 */
const DAYPARTS = [
  { k: 'morn', from: '07:00:00', to: '11:00:00' },
  { k: 'noon', from: '11:00:00', to: '17:00:00' },
  { k: 'eve', from: '17:00:00', to: '23:00:00' },
  { k: 'all', from: '00:00:00', to: '23:59:59' },
  { k: 'custom', from: null, to: null },
  { k: 'man', from: null, to: null },
];

const daypartLabel = (t, k) => t(`promos.daypart_${k}_label`);
/** morn/noon/eve/all derive their subtext from the SAME from/to fields the schedule is saved with — never a second, hand-typed copy of the time range that could drift from it. */
const daypartSub = (t, dp) => {
  if (dp.k === 'all') return t('promos.daypart_all_sub');
  if (dp.k === 'custom') return t('promos.daypart_custom_sub');
  if (dp.k === 'man') return t('promos.daypart_man_sub');
  return `${arT(dp.from)} – ${arT(dp.to)}`;
};

const SHOWS = [5, 10, 20, 30];
const EVERY_MIN = [5, 10, 15, 30];
const ROUNDS = [2, 4, 6, 10];
// index 0=Sunday, matches the platform's own week; label comes from promos.day_<key> at render time.
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const dayLabel = (t, i) => t(`promos.day_${DAY_KEYS[i]}`);

const minText = (t, v) => (v === 1 ? t('promos.unit_min_one') : v === 2 ? t('promos.unit_min_two') : t('promos.unit_min_n', { n: ar(v) }));
const secText = (t, v) => (
  v < 60 ? (v === 1 ? t('promos.unit_sec_one') : t('promos.unit_sec_n', { n: ar(v) }))
    : v === 60 ? t('promos.unit_min_one')
      : t('promos.unit_min_n', { n: ar(v / 60) })
);
const roundText = (t, r) => t('promos.unit_round_n', { n: ar(r) });
const arT = (hhmmss) => (hhmmss ? ar(hhmmss.slice(0, 5)) : '');
/** Same HH:MM-only convention as arT, for a real Date instead of a plain "HH:MM:SS" field — paused_until is a full ISO instant, not a time-of-day string. Mirrors the Remote's own pauseReturnsAt: "a duration is a promise the owner has to do arithmetic on; a time is one he can just read." Local (browser) time — same as every other clock reading already shown on this dashboard. */
const arHHMM = (date) => ar(`${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`);

/** Smart defaults — an animated design gets longer, the L-wrap (most intrusive) appears least often. */
function smartDefaults(shape, designUrls) {
  const anim = designUrls.some((u) => /\.gif($|\?)/i.test(u));
  const show = anim ? 20 : shape === 'corner' ? 8 : 10;
  const everyMin = shape === 'lshape' ? 15 : shape === 'bottom' ? 10 : 8;
  return { show, everyMin };
}

/**
 * Does [show]/[everyMin] disagree with what smartDefaults would produce for
 * [shape]/[media] right now? The ONE place this question is asked — used
 * both at open time (toDraft, deciding whether an existing promo's numbers
 * were tuned by hand and must not be silently recomputed over) and live in
 * the editor (the "smart_tuned" hint text) — so the two can never drift
 * into two slightly different definitions of "tuned."
 */
function isTunedAwayFromDefaults(shape, media, show, everyMin) {
  const def = smartDefaults(shape, media.map((m) => m.creative_url || ''));
  return show !== def.show || everyMin !== def.everyMin;
}

/**
 * The ONE description of what a rotation actually does — shared by the
 * editor's own hint and the promo-card list line, so the two can never
 * state different numbers for the same promo.
 *
 * ⚠️ CORRECTED 2026-09-10 — REVERTING A WRONG FIX FROM 2026-09-09. A جولة
 * (round) means ONE FULL PASS over every design — that is what the word
 * means and what an owner reading "٤ جولات" expects: 4 passes, not 4
 * appearances. Yesterday's session found the editor's copy disagreeing
 * with the player's behavior and "fixed" it by changing this DESCRIPTION
 * to match the player instead of asking which one matched the word's real
 * meaning — the player was wrong, the description was right, and that got
 * it backwards. The player now increments roundsUsed only when a full pass
 * completes (popuppush-app-v2, VenueOverlayPromoRotationState.
 * completesRound / MediaScreen.handleVenueOverlayPromoTick, 2026-09-10) —
 * this description reverts to match. A promo with 2 designs and a 4-round
 * cap now genuinely shows 8 appearances over 8 minutes (at a 1-minute
 * interval), not 4 over 4.
 *
 * [designCount] is required for manual mode (ignored for automatic, which
 * has no round cap) — total appearances = rounds * designCount, total time
 * = rounds * designCount * everyMin.
 */
function rotationBehaviorText(t, { auto, show, everyMin, rounds, from, to, designCount }) {
  const showText = secText(t, show);
  const everyText = minText(t, everyMin);
  if (auto) {
    return t('promos.behavior_automatic', { show: showText, every: everyText, from: arT(from), to: arT(to) });
  }
  const appearances = rounds * Math.max(1, designCount || 1);
  const totalMin = appearances * everyMin;
  return t('promos.behavior_manual', {
    show: showText, every: everyText, rounds: ar(rounds), appearances: ar(appearances), total: minText(t, totalMin),
  });
}

/** UI day index (0=Sun..6=Sat) <-> backend bit (Mon=bit0..Sun=bit6, campaign_schedules' own convention). */
const dayToBit = (uiDay) => (uiDay + 6) % 7;
const maskToDays = (mask) => (mask == null ? [0, 1, 2, 3, 4, 5, 6] : DAY_KEYS.map((_, i) => i).filter((i) => (mask >> dayToBit(i)) & 1));
const daysToMask = (days) => (days.length >= 7 ? null : days.reduce((m, d) => m | (1 << dayToBit(d)), 0));

/**
 * "Next appearance" — a PREDICTION, computed the same way the player itself
 * would (mirrors Utils.kt's getNextVenueOverlayPromoEpochSeconds exactly:
 * automatic mode is day-anchored and stateless, manual mode walks forward
 * from activated_at in interval-sized steps). Zero server cost — pure
 * arithmetic from fields the backend already stores, no query to the
 * device, no push. This is the ONLY place that does this math; the list
 * card and nothing else must call these, never reimplement the walk.
 *
 * ⚠️ NEVER assert this as observed fact — the device may be off, may have
 * missed the push, may have skipped a tick because its layer was already
 * occupied. It is what SHOULD happen if the device has been ticking
 * normally, nothing more. Every caller-facing string built from these
 * must say "المفترض أن" (supposed to / predicted to), never "يعمل الآن".
 */
function nextAutomaticAppearance(startTime, endTime, intervalSeconds, daysMask, now) {
  if (!startTime || !endTime || !intervalSeconds || intervalSeconds <= 0) return null;
  const [sh, sm, ss] = startTime.split(':').map(Number);
  const [eh, em, es] = endTime.split(':').map(Number);
  for (let dayOffset = 0; dayOffset <= 6; dayOffset++) {
    const day = new Date(now);
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() + dayOffset);
    if (daysMask != null && !((daysMask >> dayToBit(day.getDay())) & 1)) continue;

    const slotStart = new Date(day); slotStart.setHours(sh, sm, ss, 0);
    let dayEnd = new Date(day); dayEnd.setHours(eh, em, es, 0);
    if (endTime < startTime) dayEnd = new Date(dayEnd.getTime() + 24 * 3600 * 1000); // wraps midnight

    let slot = slotStart;
    while (slot < dayEnd) {
      if (slot > now) return slot;
      slot = new Date(slot.getTime() + intervalSeconds * 1000);
    }
  }
  return null;
}

/** Single source of truth for a manual promo's total appearance budget — reused by nextManualAppearance's finished check and by the grace-window fallback below so they can never disagree. */
function manualTotalAppearances(roundCap, designCount) {
  return (roundCap || 0) * Math.max(1, designCount || 1);
}

/**
 * ⚠️ SAME "round = full pass" CORRECTION AS rotationBehaviorText ABOVE, on
 * the prediction side — this must move together with the player fix or the
 * dashboard reports "finished" at the OLD (wrong) appearance count while
 * the player is still correctly running. [designCount] is required;
 * roundCap * designCount is the real total appearance budget.
 */
function nextManualAppearance(activatedAt, intervalSeconds, roundCap, designCount, now) {
  if (!activatedAt || !intervalSeconds || intervalSeconds <= 0 || !roundCap) return null;
  const perRound = Math.max(1, designCount || 1);
  const totalAppearances = manualTotalAppearances(roundCap, designCount);
  const activated = new Date(activatedAt);
  const elapsedSec = (now - activated) / 1000;
  if (elapsedSec < 0) return null; // clock skew guard, shouldn't happen
  const nextAppearanceNumber = Math.floor(elapsedSec / intervalSeconds) + 1;
  if (nextAppearanceNumber > totalAppearances) return { finished: true };
  return {
    nextAt: new Date(activated.getTime() + nextAppearanceNumber * intervalSeconds * 1000),
    roundNumber: Math.ceil(nextAppearanceNumber / perRound),
    roundCap,
  };
}

/**
 * The exact instant nextManualAppearance's own {finished:true} branch
 * crosses — used ONLY to size the grace window below before falling back to
 * the (possibly stale) clock prediction when no device report has arrived.
 * Kept as its own function, reusing manualTotalAppearances, so this can
 * never drift from the threshold nextManualAppearance itself uses.
 */
function manualPredictedFinishAt(activatedAt, intervalSeconds, roundCap, designCount) {
  const total = manualTotalAppearances(roundCap, designCount);
  return new Date(new Date(activatedAt).getTime() + total * intervalSeconds * 1000);
}

/**
 * How long to keep showing "running" (no confirmed report yet, but no
 * confident "finished" claim either) past the naive clock prediction's own
 * threshold before giving up and falling back to the hedged prediction —
 * see promoRuntimeState's own doc for why this window exists at all. Real
 * measured contention delay was ~4 minutes (two manual promos, shape-
 * dominance skip-and-defer, see promo_round_counter_bug_investigation
 * memory); 10 minutes is generous headroom over that single sample, not a
 * value tuned to it — revisit upward if a worse real case is ever observed.
 */
const MANUAL_FINISH_GRACE_MS = 10 * 60 * 1000;

/**
 * TODAY's (or, for a window that wraps past midnight, possibly YESTERDAY's
 * still-open) window-open instant, or null if [now] isn't currently inside
 * any allowed window at all. This is BOTH "is it in-window right now" AND
 * the device-side queue's own automatic-mode ranking key (see the player's
 * Utils.automaticWindowOpenInstantEpochSeconds — same wrap-past-midnight
 * handling, same days_of_week convention) — one function, so they can
 * never disagree. Used by isAutomaticInWindow below and by
 * isAutomaticPromoQueuedBehindAnother for the "ينتظر دوره" visibility.
 */
function automaticWindowOpenInstant(startTime, endTime, daysMask, now) {
  if (!startTime || !endTime) return null;
  const [sh, sm, ss] = startTime.split(':').map(Number);
  const [eh, em, es] = endTime.split(':').map(Number);
  const wraps = endTime < startTime;
  const dayAllowed = (date) => daysMask == null || ((daysMask >> dayToBit(date.getDay())) & 1);

  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const startToday = new Date(today); startToday.setHours(sh, sm, ss, 0);
  let endToday = new Date(today); endToday.setHours(eh, em, es, 0);
  if (wraps) endToday = new Date(endToday.getTime() + 24 * 3600 * 1000);
  if (dayAllowed(today) && now >= startToday && now < endToday) return startToday;

  if (wraps) {
    // A window that opened YESTERDAY and wraps past midnight can still be open now (e.g. 22:00–02:00).
    const yesterday = new Date(today.getTime() - 24 * 3600 * 1000);
    const startYesterday = new Date(yesterday); startYesterday.setHours(sh, sm, ss, 0);
    const endYesterday = new Date(startYesterday.getTime() + (endToday - startToday));
    if (dayAllowed(yesterday) && now >= startYesterday && now < endYesterday) return startYesterday;
  }
  return null;
}

/** Is "now" inside an automatic promo's own daypart window — same start/end/days fields nextAutomaticAppearance already reads, just asking a different question ("is it open right now" vs "when does it next open"). */
function isAutomaticInWindow(startTime, endTime, daysMask, now) {
  return automaticWindowOpenInstant(startTime, endTime, daysMask, now) != null;
}

/** Do two promos' device targeting overlap at all? null device_ids means "every company device" (same convention used throughout this feature) — guaranteed overlap with anything. */
function promoDeviceSetsOverlap(a, b) {
  if (!a.device_ids || !b.device_ids) return true;
  return a.device_ids.some((id) => b.device_ids.includes(id));
}

/**
 * ⚠️ A DASHBOARD PREDICTION, NOT A DEVICE-CONFIRMED FACT (2026-09-10) — same
 * honesty discipline as everywhere else in this file. The device is the
 * only party that actually knows its own queue (see the player's
 * Utils.rankVenueOverlayPromoQueue for the real ranking this approximates:
 * manual before automatic, earliest window-open instant within a type,
 * ONE QUEUE PER DEVICE — only promos sharing a target device ever compete).
 * This exists purely so an enabled, in-window promo showing nothing is
 * never SILENT about why — see the disabled_control_must_say_why memory
 * for why that silence is a whole class of bug this project keeps finding.
 */
function isAutomaticPromoQueuedBehindAnother(promo, allPromos, now) {
  if (promo.mode !== 'automatic' || !promo.is_active) return false;
  const myOpen = automaticWindowOpenInstant(promo.start_time, promo.end_time, promo.days_of_week, now);
  if (!myOpen) return false; // not even in its own window right now — that's outside_window, a different state

  return allPromos.some((other) => {
    if (other.id === promo.id || !other.is_active || !promoDeviceSetsOverlap(promo, other)) return false;
    if (other.mode === 'manual') {
      // Manual always outranks automatic — approximated via the same
      // running/finished prediction the card itself already shows, since
      // the device's real round-usage isn't exposed to the dashboard.
      return promoRuntimeState(other, now).key === 'running';
    }
    const otherOpen = automaticWindowOpenInstant(other.start_time, other.end_time, other.days_of_week, now);
    if (otherOpen == null) return false;
    // A paused automatic promo has yielded its slot too — same as the
    // manual check above, and matching the player's own
    // Utils.promoIsCurrentlyEligible (checks isPromoPaused before either
    // branch). Without this, pausing an earlier-window automatic promo
    // would leave a later one wrongly reading "queued" even though the
    // slot is actually free — see the queue-yield finding this fix pairs with.
    if (promoRuntimeState(other, now).key === 'paused') return false;
    return otherOpen < myOpen;
  });
}

const formatEta = (t, targetDate, now) => minText(t, Math.max(1, Math.round((targetDate - now) / 60000)));

/** The ONE place that counts a promo's designs — shared by every round/appearance calculation below and by the card's own "N designs" line, so they can never disagree. */
const promoDesignCount = (p) => (p.media || []).length || (p.media_ids || []).length;

/**
 * The ONE place that decides which of the real states a promo is in —
 * is_active alone is not enough (it means "permitted to run", not "is
 * running"; a manual promo that exhausted its rounds is still is_active
 * but is finished, not running). Everything the card renders (badge, info
 * line, action button) reads from this, never recomputes it independently.
 *
 *   running         — automatic: inside its window. manual: rounds not exhausted.
 *   stopped         — is_active false (never started / turned off).
 *   finished        — manual only: is_active true but its round cap is spent.
 *   outside_window  — automatic only: is_active true but not inside today's window.
 *   paused          — is_active true but paused_until is in the future (see below).
 *
 * ⚠️ 'paused' — A SIXTH STATE, CHECKED BEFORE MODE-SPECIFIC RULES (2026-09-12).
 * Timed pause (venue-overlay/promos remote-control surface) writes ONE
 * column, `paused_until`, that BOTH this dashboard and the Remote read —
 * neither has its own separate notion of "paused." Mirrors the player's own
 * `Utils.promoIsCurrentlyEligible`, which checks `isPromoPaused` first,
 * before either the manual or automatic branch, because a pause overrides
 * either one identically. Deliberately its OWN key, never reusing 'stopped'
 * (badge_paused/"متوقّف") — that already means manually, permanently turned
 * off; conflating it with a temporary, self-resolving pause would repeat the
 * exact description-vs-implementation trap this project has a standing rule
 * against (see description_vs_implementation_disagreement_lesson memory).
 * No live channel: this is only as fresh as the query's existing 30s poll
 * (see useGetPromos's own doc — built for the identical class of problem,
 * real_finished_at) or the next full page load. That's the deliberate,
 * accepted tradeoff, not an oversight.
 *
 * ⚠️ TWO SOURCES FOR "finished", NEVER BLENDED (2026-09-10). `p.real_finished_at`
 * is a FACT — the device itself confirmed its last round rendered (see
 * VenueOverlayPromo::getRealFinishedAtAttribute() on the backend and
 * promo_round_counter_bug_investigation memory for the full story). The
 * clock-only `nextManualAppearance` math below is a PREDICTION that ignores
 * contention and is proven to fire early. The real fact, when present, can
 * only ever be EQUAL TO OR LATER than the prediction (contention can only
 * delay a real completion, never move it earlier) — so whenever
 * `real_finished_at` exists, trust it outright and mark the state
 * `confirmed: true`; only fall back to the prediction (`confirmed: false`)
 * when no report has arrived. The state object always carries which source
 * answered, so the info line can say so explicitly instead of quietly
 * mixing fact and guess under one label.
 *
 * ⚠️ A GRACE WINDOW SITS BETWEEN "predicted done" AND "confirmed done"
 * (2026-09-10). The instant the naive clock crosses its own predicted finish
 * with no report yet, that is indistinguishable from "still legitimately
 * running, just delayed by contention" — the exact case this whole feature
 * exists to not misreport. So don't claim finished (even hedged) right at
 * that threshold; keep showing `running` (with no ETA — the naive math
 * already says there shouldn't be one) for MANUAL_FINISH_GRACE_MS past it.
 * Only once BOTH the naive prediction AND that grace window have passed with
 * still no device report does this degrade to the hedged "predicted done"
 * wording — the device-offline case: a report that will genuinely never
 * arrive must not leave the card frozen on "running" forever.
 *
 * ⚠️ 'queued' — A FIFTH STATE, AUTOMATIC ONLY (2026-09-10). ONE QUEUE PER
 * DEVICE means an enabled automatic promo can be sitting squarely inside
 * its own window and still show NOTHING, because a higher-priority promo
 * (a manual promo, or another automatic one whose window opened earlier —
 * see the player's Utils.rankVenueOverlayPromoQueue) currently holds the
 * device's one slot. That must never read as silent/broken — see
 * disabled_control_must_say_why memory — so it gets its own honest state
 * ("ينتظر دوره") instead of falsely claiming 'running'. [allPromos]
 * defaults to empty so existing 2-arg callers (tests, anything not passing
 * the full list) are unaffected — they simply never detect queuing.
 */
function promoRuntimeState(p, now, allPromos = []) {
  if (!p.is_active) return { key: 'stopped' };
  if (p.paused_until) {
    const until = new Date(p.paused_until);
    if (until > now) return { key: 'paused', until };
  }
  if (p.mode === 'automatic') {
    if (!isAutomaticInWindow(p.start_time, p.end_time, p.days_of_week, now)) return { key: 'outside_window' };
    if (isAutomaticPromoQueuedBehindAnother(p, allPromos, now)) return { key: 'queued' };
    const next = nextAutomaticAppearance(p.start_time, p.end_time, p.interval_seconds, p.days_of_week, now);
    return { key: 'running', next };
  }
  if (p.real_finished_at) return { key: 'finished', confirmed: true, finishedAt: new Date(p.real_finished_at) };
  const res = nextManualAppearance(p.activated_at, p.interval_seconds, p.manual_round_count, promoDesignCount(p), now);
  if (res && res.finished) {
    const predictedFinishAt = manualPredictedFinishAt(p.activated_at, p.interval_seconds, p.manual_round_count, promoDesignCount(p));
    const withinGrace = now - predictedFinishAt < MANUAL_FINISH_GRACE_MS;
    if (withinGrace) return { key: 'running', next: null };
    return { key: 'finished', confirmed: false };
  }
  return { key: 'running', next: res || null };
}

/**
 * The honestly-worded info line for a state, or null when there's nothing
 * to say (stopped). Still a PREDICTION, never an observation — "المفترض أن"
 * (supposed to), never "يعمل الآن" (is showing) — same discipline as the
 * eta math above. "outside_window" gets the window's own literal start
 * time, not a countdown — "يبدأ ١١:٠٠" is more useful than "بعد ٩ ساعات"
 * when the window opens tomorrow morning.
 *
 * ⚠️ "finished" HAD TO STOP ASSERTING FACT (2026-09-10 — this used to say
 * "انتهت جولاته", past tense, no hedge, unconditionally). Proven wrong on
 * real hardware, not hypothetically: two manual promos sharing one device,
 * one gets skipped-and-deferred by shape dominance a few times, and
 * finishes several minutes later than this clock-only calculation (which
 * knows nothing about contention) assumes. The dashboard called that promo
 * "finished" a full 4 minutes before its real last round actually fired.
 * Contention between two manual promos is a NORMAL condition (any two
 * sharing a device trigger it), not an edge case — so this can lie
 * routinely, not rarely.
 *
 * ⚠️ NOW TWO WORDINGS, NOT ONE — `state.confirmed` says which source
 * answered (see promoRuntimeState's own doc). `confirmed: true` means the
 * device itself reported this, so the past-tense assertion is finally
 * honest again ("انتهت جولاته"); `confirmed: false` keeps the hedge
 * ("المفترض أنه انتهى") because no device report has arrived yet — it is
 * still only a clock-only guess. Never blend the two into one label.
 */
function stateLineText(t, p, state, now) {
  if (state.key === 'running') {
    if (p.mode === 'automatic') {
      return state.next ? t('promos.next_appearance_automatic', { eta: formatEta(t, state.next, now) }) : null;
    }
    if (!state.next) return null;
    return t('promos.next_appearance_manual', {
      eta: formatEta(t, state.next.nextAt, now), round: ar(state.next.roundNumber), total: ar(state.next.roundCap),
    });
  }
  if (state.key === 'finished') {
    return t(state.confirmed ? 'promos.next_appearance_finished_confirmed' : 'promos.next_appearance_finished');
  }
  if (state.key === 'outside_window') return t('promos.outside_window_line', { time: arT(p.start_time) });
  if (state.key === 'queued') return t('promos.queued_line');
  if (state.key === 'paused') return t('promos.paused_until_line', { time: arHHMM(state.until) });
  return null;
}

/** Badge text for a state — icon + honest label, never claiming observed live state (see stateLineText). */
function stateBadgeText(t, p, state) {
  if (state.key === 'running') return `🟢 ${t('promos.badge_running')}`;
  if (state.key === 'finished') return `✅ ${t('promos.badge_finished')}`;
  if (state.key === 'outside_window') return `⏸️ ${t('promos.badge_outside_window')}`;
  if (state.key === 'queued') return `⏳ ${t('promos.badge_queued')}`;
  // ⚠️ OWN KEY, OWN LABEL — never badge_paused ("متوقّف", manually stopped).
  // A timed pause resumes on its own; conflating the two words would tell
  // an owner a promo needs restarting when it doesn't. See promoRuntimeState's own doc.
  if (state.key === 'paused') return `🟠 ${t('promos.badge_timed_pause')}`;
  return `⚪ ${p.mode === 'manual' ? t('promos.badge_paused') : t('promos.badge_disabled')}`;
}

// 'paused' groups visually with outside_window/queued ('waiting') rather than
// with 'stopped' ('paused' CSS class, confusingly named the same as this NEW
// state key but pre-existing) — a timed pause resumes on its own, same as
// those two, unlike a manual stop which needs a human to restart it.
const stateBadgeClass = { running: 'running', finished: 'finished', outside_window: 'waiting', queued: 'waiting', paused: 'waiting', stopped: 'paused' };

/**
 * ⚠️ NO LONGER BRANCHES ON finished vs. not (2026-09-10). The old
 * "finished" wording existed only to fix a TENSE mismatch (present-tense
 * verbs describing a promo that had already stopped) — the new manual
 * copy is verb-free ("{{rounds}} جولات · {{appearances}} ظهورات ·
 * {{total}}", just the configured totals), so there is no tense to get
 * wrong and no separate wording needed. The badge + info line already
 * carry the finished-ness (✅ انتهى / "انتهت جولاته") in past tense; this
 * line just states the numbers, which are equally true whether the promo
 * is still running or has completed exactly that many rounds.
 */
function rotationSummaryText(t, p) {
  const everyMin = Math.round((p.interval_seconds || 0) / 60) || 1;
  return rotationBehaviorText(t, {
    auto: p.mode === 'automatic', show: p.show_duration_seconds, everyMin,
    rounds: p.manual_round_count || 0, from: p.start_time, to: p.end_time, designCount: promoDesignCount(p),
  });
}

/**
 * A stored VenueOverlayPromo row -> the draft shape this editor works with.
 *
 * ⚠️ `part` matches a FIXED preset (morn/noon/eve/all) only when start_time/
 * end_time match it EXACTLY — never a guessed daypart (e.g. "الصباح") for a
 * schedule that doesn't match, which would silently misrepresent the real,
 * saved hours as something they are not. Anything automatic that doesn't
 * match a fixed preset is 'custom' (2026-09-10 — this used to show NO
 * button selected at all, with a hint telling the owner to pick a DIFFERENT
 * preset to change it, destroying their real hours; now 'custom' is
 * selected and the actual saved from/to are shown, editable, in the time
 * fields below).
 */
/**
 * [libraryById] is an OPTIONAL Map<id, {file_name, path}> — whatever's
 * already cached from useGetPromoDesignLibrary at the moment a promo is
 * opened (often non-empty: the same 'promoDesignLibrary' query the picker
 * uses, shared cache key, so anyone who's opened the picker or another
 * promo this session already has it warm). Resolving media names HERE,
 * synchronously, rather than only via the async patch-up effect in
 * Promos(), matters for isTunedAwayFromDefaults below: smartDefaults'
 * animated-GIF detection reads creative_url, and an unresolved (null) url
 * can never be detected as animated — with a cold cache this still opens
 * correctly (never a data-loss direction: an animated promo evaluated as
 * non-animated at this instant can only make `tuned` come out TRUE when
 * the promo was actually untouched, i.e. show manual mode unnecessarily —
 * never silently overwrite a real value, which is the failure this fix
 * exists to prevent). The async effect in Promos() still runs afterward
 * regardless, to fix up display names/urls once the library loads even on
 * a cold cache.
 */
function toDraft(p, libraryById = new Map()) {
  const auto = p.mode === 'automatic';
  const fixedPresets = DAYPARTS.filter((d) => d.k !== 'custom' && d.k !== 'man');
  const matched = auto ? fixedPresets.find((d) => d.from === p.start_time && d.to === p.end_time) : null;
  const dp = auto ? (matched ? matched.k : 'custom') : 'man';
  const media = (p.media || []).length
    ? p.media
    : (p.media_ids || []).map((id) => {
        const found = libraryById.get(id);
        return found ? { id, file_name: found.file_name, creative_url: found.path } : { id, creative_url: null };
      });
  const show = p.show_duration_seconds;
  const everyMin = Math.round((p.interval_seconds || 0) / 60) || 1;

  // ⚠️ NEVER RECOMPUTE OVER A VALUE THE USER SET HIMSELF (2026-09-12).
  // recomputeIfAuto (below) reruns smartDefaults whenever the shape or the
  // design list changes, short-circuiting only when draft.adv is already
  // true — this used to ALWAYS open an existing promo with adv:false, so
  // editing one to add a single design silently replaced a deliberately
  // tuned show/every with the computed defaults. Real incident: Mahmoud set
  // a custom interval, opened the promo only to add content, and the save
  // wiped his numbers back to smartDefaults's.
  //
  // isTunedAwayFromDefaults compares the STORED show/everyMin against what
  // smartDefaults would produce for this promo's CURRENT shape+media right
  // now: if they disagree, a human changed them at some point (the only way
  // they COULD disagree, since these are the only two ways show/everyMin are
  // ever set) — open in manual mode so recomputeIfAuto leaves them alone.
  // If they agree, the promo is either brand new or has never been tuned
  // away from the defaults for its current shape/media — defaults keep
  // applying automatically, same as before.
  const tuned = isTunedAwayFromDefaults(p.shape, media, show, everyMin);

  return {
    id: p.id,
    name: p.name || '',
    shape: p.shape,
    media,
    deviceIds: p.device_ids || null, // null = all
    isActive: p.is_active !== false,
    show,
    everyMin,
    auto,
    from: p.start_time,
    to: p.end_time,
    days: maskToDays(p.days_of_week),
    rounds: p.manual_round_count || ROUNDS[1],
    part: dp,
    adv: tuned,
  };
}

function toPayload(d) {
  return {
    name: d.name.trim(),
    shape: d.shape,
    media_ids: d.media.map((m) => m.id),
    device_ids: d.deviceIds,
    show_duration_seconds: d.show,
    interval_seconds: d.everyMin * 60,
    mode: d.auto ? 'automatic' : 'manual',
    start_time: d.auto ? d.from : null,
    end_time: d.auto ? d.to : null,
    days_of_week: d.auto ? daysToMask(d.days) : null,
    manual_round_count: d.auto ? null : d.rounds,
    is_active: d.isActive,
  };
}

function newDraft() {
  const shape = 'lshape';
  const dd = smartDefaults(shape, []);
  return {
    id: null, name: '', shape, media: [], deviceIds: null, isActive: true,
    show: dd.show, everyMin: dd.everyMin, auto: true, from: DAYPARTS[0].from, to: DAYPARTS[0].to,
    days: [0, 1, 2, 3, 4, 5, 6], rounds: ROUNDS[1], part: DAYPARTS[0].k, adv: false,
  };
}

function frameStyle(shapeKey, showPromo) {
  const s = SHAPES[shapeKey];
  return showPromo ? s.frame.content : { inset: 0 };
}
export {
  ar, arT, arHHMM, shapeLabel, shapeMeta, daypartLabel, daypartSub, dayLabel,
  minText, secText, roundText, promoRuntimeState, stateLineText, stateBadgeText, rotationSummaryText,
  nextManualAppearance, rotationBehaviorText, promoDesignCount,
  isAutomaticPromoQueuedBehindAnother, automaticWindowOpenInstant,
  DAYPARTS, toDraft, toPayload, isAutomaticInWindow, nextAutomaticAppearance,
  smartDefaults, isTunedAwayFromDefaults, SHAPES, ROUNDS, DAY_KEYS, maskToDays, daysToMask,
  stateBadgeClass, newDraft,
};
